import type { ApiMeta, ApiResponse } from '@shared/contracts/mockApi';
import { db } from './db';
import { ERROR_CODES, MockApiError } from './errors';

/**
 * Transport layer for the mock Tribe Adapter.
 *
 * Owns the shared response envelope, request IDs, deterministic latency and
 * scenario-driven failure injection, so every endpoint behaves consistently and
 * no feature module invents its own error handling.
 */

let requestCounter = 0;

export function nextRequestId(): string {
  requestCounter += 1;
  return `req_${String(requestCounter).padStart(8, '0')}`;
}

/** Latency profile per scenario, in milliseconds. Deterministic, not random. */
function latencyFor(): number {
  switch (db().scenario) {
    case 'slow':
      return 1400;
    case 'large-dataset':
      return 420;
    default:
      return 140;
  }
}

/**
 * Tests need real state transitions without real waiting. When disabled the
 * adapter still yields to the microtask queue, so loading states still render.
 */
let latencyEnabled = true;
export function setLatencyEnabled(enabled: boolean): void {
  latencyEnabled = enabled;
}

function delay(ms: number): Promise<void> {
  if (!latencyEnabled || ms <= 0) return Promise.resolve();
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export type EndpointOptions = {
  /** Endpoints that only read are still blocked offline, but never mutate. */
  readonly mutating?: boolean;
  /** Skip scenario failure injection (used by demo control endpoints). */
  readonly bypassScenario?: boolean;
};

/**
 * Global preconditions applied to every adapter call, in the order a real
 * gateway would apply them.
 */
function assertTransportPreconditions(options: EndpointOptions): void {
  if (db().offline) {
    throw new MockApiError(
      ERROR_CODES.OFFLINE,
      'You appear to be offline. Check your connection and try again.',
      { retryable: true },
    );
  }
  if (options.bypassScenario) return;

  switch (db().scenario) {
    case 'provider-unavailable':
      throw new MockApiError(
        ERROR_CODES.PROVIDER_UNAVAILABLE,
        'We could not reach your bank right now. Please try again in a moment.',
        { retryable: true },
      );
    case 'permission-denied':
      throw new MockApiError(
        ERROR_CODES.PERMISSION_DENIED,
        'You do not have permission to do this.',
      );
    case 'ai-unavailable':
      // Only AI endpoints opt into this; see aiGuard below.
      break;
    default:
      break;
  }
}

/** AI endpoints call this so the ai-unavailable scenario is targeted. */
export function assertAiAvailable(): void {
  if (db().scenario === 'ai-unavailable') {
    throw new MockApiError(
      ERROR_CODES.AI_UNAVAILABLE,
      'The assistant is unavailable right now. Your layout has not changed.',
      { retryable: true },
    );
  }
}

/**
 * Wrap an endpoint implementation in the shared envelope.
 * Throws MockApiError; callers translate that into a UI state.
 */
export async function endpoint<T>(
  fn: () => T | Promise<T>,
  options: EndpointOptions = {},
  meta?: Omit<ApiMeta, 'requestId'>,
): Promise<ApiResponse<T>> {
  await delay(latencyFor());
  assertTransportPreconditions(options);
  const data = await fn();
  return {
    data,
    meta: { requestId: nextRequestId(), ...(meta ?? {}) },
  };
}

/** Convenience for endpoints whose callers only want the payload. */
export async function unwrap<T>(promise: Promise<ApiResponse<T>>): Promise<T> {
  const response = await promise;
  if (response.error) {
    throw new MockApiError(ERROR_CODES.VALIDATION_ERROR, response.error.message);
  }
  return response.data;
}

/** Weak etag over a JSON-serialisable value. Stable for identical content. */
export function etagOf(value: unknown): string {
  const json = JSON.stringify(value);
  let hash = 2166136261;
  for (let i = 0; i < json.length; i += 1) {
    hash ^= json.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return `W/"${(hash >>> 0).toString(16)}"`;
}
